import { useEffect, useRef } from 'react'

// ---------------------------------------------------------------------------
// Body scroll lock (compteur global pour panneaux empilés).
// ---------------------------------------------------------------------------
let bodyLockCount = 0
let savedScrollY = 0
let savedBodyStyles = null

function lockBodyScroll() {
  if (typeof document === 'undefined') return
  bodyLockCount += 1
  if (bodyLockCount > 1) return
  savedScrollY = window.scrollY || window.pageYOffset || 0
  const body = document.body
  savedBodyStyles = {
    position: body.style.position,
    top: body.style.top,
    width: body.style.width,
    overflow: body.style.overflow,
  }
  // Technique iOS-safe : on fige la position du body au lieu d'un simple
  // overflow:hidden, sinon Safari mobile garde le rubber-band sur le body.
  body.style.position = 'fixed'
  body.style.top = `-${savedScrollY}px`
  body.style.width = '100%'
  body.style.overflow = 'hidden'
}

function unlockBodyScroll() {
  if (typeof document === 'undefined') return
  bodyLockCount = Math.max(0, bodyLockCount - 1)
  if (bodyLockCount > 0) return
  const body = document.body
  if (savedBodyStyles) {
    body.style.position = savedBodyStyles.position
    body.style.top = savedBodyStyles.top
    body.style.width = savedBodyStyles.width
    body.style.overflow = savedBodyStyles.overflow
    savedBodyStyles = null
  }
  window.scrollTo(0, savedScrollY)
}

// ---------------------------------------------------------------------------
// Gestion du bouton retour : UNE seule pile centrale + UN seul listener
// `popstate` global. Ça évite le bug "chaque hook actif consomme le même
// popstate et ferme tous les panneaux d'un coup", qui rendait l'ouverture
// des fiches instable dès qu'un autre panneau venait d'être fermé.
// ---------------------------------------------------------------------------
/** @type {{ id: string, onClose: () => void }[]} */
const stack = []
/** Nombre de `popstate` que notre propre `history.back()` (cleanup manuel)
 * va générer et qu'il faut ignorer pour ne pas fermer un panneau parent. */
let expectingBack = 0
let listenerInstalled = false

function nextId() {
  return `__back_close_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
}

function ensureListener() {
  if (listenerInstalled) return
  if (typeof window === 'undefined' || !window.history) return
  listenerInstalled = true
  window.addEventListener('popstate', () => {
    // 1. Popstate généré par notre cleanup (history.back) → absorber, sans
    //    déclencher de onClose.
    if (expectingBack > 0) {
      expectingBack -= 1
      return
    }
    // 2. Vrai retour utilisateur → fermer UNIQUEMENT le panneau au sommet.
    const top = stack.pop()
    if (!top) return
    try {
      top.onClose?.()
    } catch {
      // no-op
    }
  })
}

/**
 * Combine deux comportements indispensables sur mobile pour les drawers /
 * modales / panneaux plein écran :
 *
 *  1. **Bouton retour système → ferme le panneau** au lieu de quitter l'app.
 *     Un seul listener `popstate` global ferme le panneau LE PLUS RÉCEMMENT
 *     ouvert (LIFO), peu importe combien de hooks sont actifs en même
 *     temps. Les fermetures manuelles (clic ✕) retirent leur entrée
 *     d'historique via `history.back()` et le popstate associé est ignoré.
 *
 *  2. **Verrouillage du scroll body** (iOS-safe via `position: fixed`),
 *     avec restauration de la position de scroll initiale à la fermeture.
 *     Compteur global pour les panneaux empilés.
 *
 * @param {boolean} isOpen — vrai quand le panneau est affiché
 * @param {() => void} onClose — callback appelé sur retour système
 */
export function useBackToClose(isOpen, onClose) {
  // Stable ref vers onClose : évite que le useEffect se ré-exécute (et donc
  // empile des entrées d'historique en boucle) à chaque render parent.
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (!isOpen) return undefined
    if (typeof window === 'undefined' || !window.history) return undefined

    lockBodyScroll()
    ensureListener()

    const entry = {
      id: nextId(),
      onClose: () => {
        try {
          onCloseRef.current && onCloseRef.current()
        } catch {
          // no-op
        }
      },
    }
    stack.push(entry)
    let pushed = false
    try {
      window.history.pushState({ __backCloseId: entry.id }, '')
      pushed = true
    } catch {
      // pushState peut échouer (iframe sandbox, etc.) — on continue sans.
    }

    return () => {
      // Cleanup déclenché par fermeture manuelle (clic ✕, navigation,
      // démontage). On retire notre entrée de la pile et, si on a réussi
      // à pousser un état, on consomme cette entrée d'historique via
      // history.back(). Le popstate qui en résulte sera absorbé par le
      // compteur `expectingBack`.
      const idx = stack.lastIndexOf(entry)
      if (idx >= 0) stack.splice(idx, 1)
      if (pushed && window.history.state?.__backCloseId === entry.id) {
        expectingBack += 1
        try {
          window.history.back()
        } catch {
          expectingBack = Math.max(0, expectingBack - 1)
        }
      }
      unlockBodyScroll()
    }
  }, [isOpen])
}
